import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import EmployeeOverviewTab from './tabs/EmployeeOverviewTab';
import EmployeeSystemsTab from './tabs/EmployeeSystemsTab';
import EmployeeAssignmentsTab from './tabs/EmployeeAssignmentsTab';

/**
 * TabPanel component for rendering tab content
 */
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

/**
 * Employee detail panel with tabs for Overview, Systems, and Assignments
 * @param {Object} props - Component props
 * @param {Object} props.employee - Selected employee data
 * @param {boolean} props.open - Whether panel is open
 * @param {Function} props.onClose - Handler for closing panel
 * @param {Function} props.onUpdate - Handler for updating employee
 * @param {boolean} props.loading - Loading state
 * @returns {React.Component} Employee detail panel
 */
function EmployeeDetailPanel({ 
  employee, 
  open, 
  onClose, 
  onUpdate,
  loading = false 
}) {
  const [tabValue, setTabValue] = useState(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Reset tab when employee changes
  useEffect(() => {
    setTabValue(0);
    setUnsavedChanges(false);
  }, [employee?.id]);

  if (!employee) return null;

  const handleTabChange = (event, newValue) => {
    if (unsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to switch tabs?');
      if (!confirmed) return;
    }
    setTabValue(newValue);
    setUnsavedChanges(false);
  };

  const handleClose = () => {
    if (unsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
    setUnsavedChanges(false);
  };

  const handleUnsavedChanges = (hasChanges) => {
    setUnsavedChanges(hasChanges);
  };

  const fullName = `${employee.firstName} ${employee.lastName}`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { 
          width: '50%', 
          minWidth: '600px',
          maxWidth: '900px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Employee Details
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Status Bar with Chips */}
      <Box sx={{ px: 2, py: 1, backgroundColor: 'grey.50', display: 'flex', gap: 1 }}>
        <Chip
          label={employee.isActive !== false ? 'Active' : 'Inactive'}
          color={employee.isActive !== false ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
        {employee.userId && (
          <Chip
            label="RadixDash User"
            color="primary"
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ px: 2 }}
        >
          <Tab label="Overview" />
          <Tab label="Systems" />
          <Tab label="Assignments" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TabPanel value={tabValue} index={0}>
          <EmployeeOverviewTab
            employee={employee}
            onUpdate={onUpdate}
            onUnsavedChanges={handleUnsavedChanges}
            loading={loading}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <EmployeeSystemsTab
            employee={employee}
            onUpdate={onUpdate}
            loading={loading}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <EmployeeAssignmentsTab
            employee={employee}
            loading={loading}
          />
        </TabPanel>
      </Box>
    </Drawer>
  );
}

EmployeeDetailPanel.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    employmentType: PropTypes.string,
    hireDate: PropTypes.string,
    isActive: PropTypes.bool,
    userId: PropTypes.string,
    assignmentCount: PropTypes.number,
    assignments: PropTypes.array
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
};

export default EmployeeDetailPanel;